import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { Upload } from "lucide-react";
import { type FormEvent, useEffect, useId, useState } from "react";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import type { AudienceVoteContestantDTO } from "../api/client";
import { uploadContestantPhotoFile } from "../lib/upload-contestant-photo";
import { resolveContestantPhotoPreviewUrl } from "./resolve-contestant-photo-preview";

type Draft = {
  displayName: string;
  subtitle: string;
  panelLabel: string;
  photoUrl: string;
  sortOrder: string;
  status: "draft" | "live" | "withdrawn";
};

const EMPTY: Draft = {
  displayName: "",
  subtitle: "",
  panelLabel: "",
  photoUrl: "",
  sortOrder: "0",
  status: "draft",
};

const PHOTO_ACCEPT = "image/jpeg,image/png,image/webp,image/gif";

function draftFromContestant(c: AudienceVoteContestantDTO): Draft {
  return {
    displayName: c.displayName,
    subtitle: c.subtitle ?? "",
    panelLabel: c.panelLabel ?? "",
    photoUrl: c.photoUrl ?? "",
    sortOrder: String(c.sortOrder),
    status: c.status,
  };
}

type ContestantBody = {
  displayName: string;
  subtitle: string | null;
  panelLabel: string | null;
  photoUrl: string | null;
  sortOrder: number;
  status: Draft["status"];
};

type AudienceVoteContestantDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  canWrite: boolean;
  contestant: AudienceVoteContestantDTO | null;
  nextSortOrder: number;
  isPending: boolean;
  onCreate: (body: ContestantBody) => void | Promise<void>;
  onUpdate: (
    contestantId: string,
    body: ContestantBody,
  ) => void | Promise<void>;
};

export function AudienceVoteContestantDialog({
  open,
  onOpenChange,
  eventId,
  canWrite,
  contestant,
  nextSortOrder,
  isPending,
  onCreate,
  onUpdate,
}: AudienceVoteContestantDialogProps) {
  const isEdit = !!contestant;
  const fileInputId = useId();
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [pendingPhotoFile, setPendingPhotoFile] = useState<File | null>(null);
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(
    null,
  );
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPendingPhotoFile(null);
    if (contestant) {
      setDraft(draftFromContestant(contestant));
    } else {
      setDraft({ ...EMPTY, sortOrder: String(nextSortOrder) });
    }
  }, [open, contestant, nextSortOrder]);

  useEffect(() => {
    if (!pendingPhotoFile) {
      setPendingPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingPhotoFile);
    setPendingPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingPhotoFile]);

  const savedPhotoRef = draft.photoUrl.trim();
  const photoPreviewQuery = useQuery({
    queryKey: ["audience-vote-contestant-photo", eventId, savedPhotoRef],
    queryFn: () => resolveContestantPhotoPreviewUrl(eventId, savedPhotoRef),
    enabled: open && savedPhotoRef.length > 0 && !pendingPhotoFile,
    staleTime: 5 * 60 * 1000,
  });

  const previewSrc = pendingPreviewUrl ?? photoPreviewQuery.data ?? null;
  const hasPhoto = !!previewSrc || (!!savedPhotoRef && !pendingPhotoFile);
  const showLoadingSavedPreview =
    !pendingPhotoFile &&
    savedPhotoRef.length > 0 &&
    photoPreviewQuery.isLoading;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const displayName = draft.displayName.trim();
    if (!displayName) return;

    const sortOrder = Number.parseInt(draft.sortOrder, 10);
    let photoUrl: string | null = savedPhotoRef || null;

    setUploadingPhoto(true);
    try {
      if (pendingPhotoFile) {
        photoUrl = await uploadContestantPhotoFile(eventId, pendingPhotoFile);
      } else if (!savedPhotoRef) {
        photoUrl = null;
      }

      const body: ContestantBody = {
        displayName,
        subtitle: draft.subtitle.trim() || null,
        panelLabel: draft.panelLabel.trim() || null,
        photoUrl,
        sortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
        status: draft.status,
      };

      if (contestant) {
        await onUpdate(contestant.id, body);
      } else {
        await onCreate(body);
      }
    } catch {
      toast.error(pendingPhotoFile ? "ອັບໂຫຼດຮູບຫຼືບັນທຶກບໍ່ສຳເລັດ" : "ບັນທຶກບໍ່ສຳເລັດ");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const busy = isPending || uploadingPhoto;

  const clearPhoto = () => {
    setPendingPhotoFile(null);
    setDraft((d) => ({ ...d, photoUrl: "" }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(90vh,720px)] max-w-md flex-col gap-0 p-0">
        <form className="flex min-h-0 flex-1 flex-col" onSubmit={handleSubmit}>
          <DialogHeader className="shrink-0 space-y-1.5 px-6 pt-6">
            <DialogTitle>{isEdit ? "ແກ້ໄຂນັກແຂ່ງ" : "ເພີ່ມນັກແຂ່ງ"}</DialogTitle>
            <DialogDescription>
              ຊື່ທີ່ສະແດງໃນ LIHF · ຮູບຜົນງານຈະອັບໂຫຼດເມື່ອກົດບັນທຶກ
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
            <div className="grid gap-3">
              <div className="space-y-2">
                <Label htmlFor="av-display-name">ຊື່ສະແດງ *</Label>
                <Input
                  id="av-display-name"
                  value={draft.displayName}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, displayName: e.target.value }))
                  }
                  required
                  disabled={!canWrite || busy}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="av-subtitle">ຄຳອະທິບາຍ</Label>
                <Input
                  id="av-subtitle"
                  value={draft.subtitle}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, subtitle: e.target.value }))
                  }
                  disabled={!canWrite || busy}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="av-panel">ປ້າຍ panel</Label>
                <Input
                  id="av-panel"
                  value={draft.panelLabel}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, panelLabel: e.target.value }))
                  }
                  placeholder="A1"
                  disabled={!canWrite || busy}
                />
              </div>

              <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
                <div>
                  <Label>ຮູບຜົນງານ</Label>
                  <p className="mt-1 text-muted-foreground text-xs">
                    JPEG, PNG, WebP ຫຼື GIF — ອັບໂຫຼດຕອນກົດບັນທຶກ
                  </p>
                </div>
                {previewSrc ? (
                  <img
                    src={previewSrc}
                    alt=""
                    className="max-h-44 w-full rounded-md border bg-background object-contain"
                  />
                ) : showLoadingSavedPreview ? (
                  <p className="text-muted-foreground text-xs">
                    ກຳລັງໂຫຼດ preview…
                  </p>
                ) : (
                  <div className="flex h-28 items-center justify-center rounded-md border border-dashed bg-background text-muted-foreground text-sm">
                    ຍັງບໍ່ມີຮູບ
                  </div>
                )}
                {canWrite ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      id={fileInputId}
                      type="file"
                      accept={PHOTO_ACCEPT}
                      className="hidden"
                      disabled={busy}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setPendingPhotoFile(file);
                        e.target.value = "";
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      disabled={busy}
                      onClick={() =>
                        document.getElementById(fileInputId)?.click()
                      }
                    >
                      <Upload className="size-4" />
                      <span className="ml-2">ເລືອກຮູບ</span>
                    </Button>
                    {hasPhoto || pendingPhotoFile ? (
                      <Button
                        type="button"
                        variant="outline"
                        disabled={busy}
                        onClick={clearPhoto}
                      >
                        ລຶບຮູບ
                      </Button>
                    ) : null}
                    {pendingPhotoFile ? (
                      <p className="w-full text-muted-foreground text-xs">
                        ຈະອັບໂຫຼດ: {pendingPhotoFile.name}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="av-sort">ລຳດັບ</Label>
                  <Input
                    id="av-sort"
                    type="number"
                    min={0}
                    value={draft.sortOrder}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, sortOrder: e.target.value }))
                    }
                    disabled={!canWrite || busy}
                  />
                </div>
                <div className="space-y-2">
                  <Label>ສະຖານະ</Label>
                  <SimpleSelect
                    value={draft.status}
                    onValueChange={(v) =>
                      setDraft((d) => ({
                        ...d,
                        status: v as Draft["status"],
                      }))
                    }
                    disabled={!canWrite || busy}
                    options={[
                      { value: "draft", label: "Draft" },
                      { value: "live", label: "Live" },
                      { value: "withdrawn", label: "Withdrawn" },
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 gap-2 border-t px-6 py-4">
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={busy || !canWrite}>
              {uploadingPhoto ? "ກຳລັງອັບໂຫຼດ…" : isEdit ? "ບັນທຶກ" : "ເພີ່ມ"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
